import { useState, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ButtonLoading } from '@/components/ui/button';
import { Button } from '@/components/ui/button';
import { InputWithLabel } from '@/components/ui/input';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { QUERY_GET_CONVERSATION } from '@/graphql/queries/chatKnowledge';
import { MUTATION_UPDATE_CONVERSATION_TITLE } from '@/graphql/mutation/chatKnowledge';
import type { Conversation, Message } from '@/graphql/types/chatKnowledge';

interface ViewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string | null;
}

const validationSchema = yup.object().shape({
  title: yup.string().optional(),
});

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export const ViewDialog = ({
  open,
  onOpenChange,
  conversationId,
}: ViewDialogProps) => {
  const apolloClient = useApolloClient();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);

  useEffect(() => {
    if (open && conversationId) {
      fetchConversation();
    } else {
      setConversation(null);
    }
  }, [open, conversationId]);

  const fetchConversation = async () => {
    if (!conversationId) return;

    try {
      setLoading(true);
      const { data } = await apolloClient.query<{ getConversation: Conversation }>({
        query: QUERY_GET_CONVERSATION,
        variables: { conversation_id: conversationId },
      });
      setConversation(data.getConversation);
    } catch (error) {
      console.error('Error fetching conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTitle = async (title: string) => {
    if (!conversationId) return;

    try {
      setLoadingUpdate(true);
      await apolloClient.mutate<{ updateConversationTitle: Conversation }>({
        mutation: MUTATION_UPDATE_CONVERSATION_TITLE,
        variables: {
          conversation_id: conversationId,
          title,
        },
      });
      await fetchConversation();
    } catch (error) {
      console.error('Error updating title:', error);
    } finally {
      setLoadingUpdate(false);
    }
  };


  if (!conversation && !loading) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-6 max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader className="w-fit">
          <DialogTitle>Detalhes da Conversa</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center p-8">Carregando...</div>
        ) : conversation ? (
          <div className="flex flex-col gap-6">
            <Formik
              initialValues={{ title: conversation.title || '' }}
              validationSchema={validationSchema}
              onSubmit={async (values) => {
                await handleUpdateTitle(values.title);
              }}
            >
              {({ values, setFieldValue, handleSubmit }) => (
                <Form className="flex flex-col gap-4">
                  <InputWithLabel
                    label="Título da Conversa"
                    id="title"
                    name="title"
                    value={values.title}
                    onChange={(e) => setFieldValue('title', e.target.value)}
                    className="w-full max-w-full"
                  />
                  <ButtonLoading
                    type="submit"
                    variant="outline"
                    label="Atualizar Título"
                    loading={loadingUpdate}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmit();
                    }}
                  />
                </Form>
              )}
            </Formik>

            <div className="flex flex-col gap-4">
              <div className="text-sm text-gray-600">
                <p><strong>Município:</strong> {conversation.municipality_code}</p>
                <p><strong>Usuário:</strong> {conversation.user_id}</p>
                <p><strong>Criado em:</strong> {formatDate(conversation.created_at)}</p>
                <p><strong>Atualizado em:</strong> {formatDate(conversation.updated_at)}</p>
              </div>

              <div className="flex flex-col gap-3">
                <h3 className="font-semibold">Mensagens ({conversation.messages.length})</h3>
                <div className="flex flex-col gap-3 max-h-96 overflow-y-auto">
                  {conversation.messages.map((message: Message) => (
                    <div
                      key={message.id}
                      className={`p-3 rounded-lg ${
                        message.role === 'user'
                          ? 'bg-blue-50 ml-8'
                          : 'bg-gray-50 mr-8'
                      }`}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-semibold text-sm">
                          {message.role === 'user' ? 'Usuário' : 'Assistente'}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(message.created_at)}
                        </span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : null}

        <DialogFooter className="flex-col">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
